import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  FlatList,
  ActivityIndicator,
  StyleSheet,
  Image,
  ScrollView,
  TouchableWithoutFeedback,
  SafeAreaView,
  Platform,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import * as hotelActions from '../store/actions/hotel';
import { moveNext } from '../store/actions/workflow';
import TabBar from '../components/SegmentedControl/TabBar';
import SegmentedControl from '../components/SegmentedControl/SegmentedControl';
import Swiper from 'react-native-web-swiper';
import { WebView } from 'react-native-webview';
import BaseText from '../components/BaseText';
import ShadowBox from '../components/ShadowBox';
import { HotelRoomCard, HotelDetailHeader } from '../components/resultCards';
import MapView from 'react-native-maps';
import Icon from '../components/Icon';

import {
  selectSelectedHotel,
  selectHotelDetail,
  selectHotelDetailSliderPhotos,
  selectHotelDetailAmenities,
  selectHotelSearchType,
} from '../store/selectors/hotel.selector';

import { Color, CommonProperties, CommonStyles } from '../styles';

import moment from 'moment';
import { scale } from '../utils/scale.utils';
import RowView from '../components/RowView';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import PointMarker from '../components/maps/PointMarker';
import { useDateFormat } from '../utils/useDateFormat';
import {
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
} from '../store/selectors/auth.selector';
import { formatCurrency } from '../utils/currency.utils';

const FromToDate = memo((props) => {
  const { toDate, fromDate } = props;
  const formatDate = useDateFormat();

  return (
    <BaseText style={CommonStyles.small}>
      {formatDate(fromDate)} - {formatDate(toDate)}
    </BaseText>
  );
});

const HotelDetailScreen = ({ navigation }) => {
  const { t } = useTranslation(i18nNS.hotel);
  const { tKey } = useComponentTKey('HotelDetailScreen');
  const dispatch = useDispatch();
  const selectedHotel = useSelector(selectSelectedHotel);
  const hotelDetail = useSelector(selectHotelDetail);
  const photos = useSelector(selectHotelDetailSliderPhotos);
  const hotelAmenities = useSelector(selectHotelDetailAmenities);

  const [hotelDetailLoading, setHotelDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [roomType, setRoomType] = useState('');
  const [roomTypeSegments, setRoomTypeSegments] = useState([]);
  const [viewMap, setViewMap] = useState(false);
  const [tripadvisorVisited, setTripadvisorVisited] = useState(false);
  const [activeDataForList, setActiveDataForList] = useState(payLaterResults);
  const [showCorpMessage, setShowCorpMessage] = useState(false);
  const hotelDescription = useMemo(() => hotelDetail?.VFML?.replace(/<[^>]+>/g, ''), [hotelDetail]);

  const defaultResults = hotelDetail?.HotelTravelComponentDetailSegments;
  const payLaterResults = hotelDetail?.PayLaterHotelTravelComponentDetailSegments;
  const payNowResults = hotelDetail?.PayNowHotelTravelComponentDetailSegments;
  const corporateRates = hotelDetail?.CorporateHotelTravelComponentDetailSegments;
  const governmentRates = hotelDetail?.GovernmentRateComponentDetailSegments;
  const tripAdvisorUrl =
    hotelDetail &&
    'https://www.tripadvisor.com/WidgetEmbed-cdspropertysummary?locationId=' +
      hotelDetail.HotelsComHotelId +
      '&partnerId=E20B5F1564E146B696857621F622D268&display=true';
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const hotelSearchType = useSelector(selectHotelSearchType);
  useEffect(() => {
    if (hotelDetail) {
      const { IsSuppressPayLater, IsSuppressPayNow } = selectedHotel;
      const roomTypeOptions = [];
      let payNowLowestCost;
      let payLaterLowestCost;
      if (payNowResults?.length) {
        payNowLowestCost = Math.round(payNowResults[0]?.PerNightCost);
        roomTypeOptions.push({
          value: 'payNow',
          label: t('HotelScreen.PayNow'),
          price: payNowLowestCost,
          disable: IsSuppressPayNow,
          //style: IsSuppressPayNow ? styles.disabledSegmentControl : null,
        });
      }
      if (payLaterResults?.length) {
        payLaterLowestCost = Math.round(payLaterResults[0].PerNightCost);
        roomTypeOptions.push({
          value: 'payLater',
          label: t('HotelScreen.PayLater'),
          price: payLaterLowestCost,
          disable: IsSuppressPayLater,
          //style: IsSuppressPayLater ? styles.disabledSegmentControl : null,
        });
      }
      if (corporateRates?.length) {
        roomTypeOptions.push({
          value: 'corporate',
          label: t('HotelScreen.Corporate'),
          disable: IsSuppressPayLater,
          price: Math.round(corporateRates[0].PerNightCost),
        });
      }
      if (governmentRates?.length) {
        roomTypeOptions.push({
          value: 'government',
          label: t('HotelScreen.Government'),
          disable: IsSuppressPayLater,
          price: Math.round(governmentRates[0].PerNightCost),
        });
      }
      setRoomTypeSegments(roomTypeOptions);
      setShowCorpMessage(hotelDetail.IsShowCorporateDiv);
      if (corporateRates?.length) {
        setRoomType('corporate');
        setActiveDataForList(corporateRates);
      } else if (payNowResults?.length || payLaterResults?.length) {
        const payNowLower =
          payNowLowestCost && (!payLaterLowestCost || payNowLowestCost <= payLaterLowestCost);
        if (payNowResults?.length && payNowLower && hotelSearchType !== 'payLater') {
          setRoomType('payNow');
          setActiveDataForList(payNowResults);
        } else {
          setRoomType('payLater');
          setActiveDataForList(payLaterResults);
        }
      } else {
        setRoomType('default');
        setActiveDataForList(defaultResults);
      }
    }
  }, [
    hotelDetail,
    defaultResults,
    payNowResults,
    payLaterResults,
    corporateRates,
    governmentRates,
    selectedHotel,
    t,
  ]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={CommonStyles.centerItems}>
          <BaseText>{t(tKey('ChooseRoom'))}</BaseText>
          <FromToDate fromDate={selectedHotel?.CheckInDate} toDate={selectedHotel?.CheckOutDate} />
        </View>
      ),
      headerStyle: {
        elevation: 0,
        shadowOpacity: 0,
        borderBottomWidth: 0,
      },
      headerTitleAlign: 'center',
    });
  }, [navigation, selectedHotel, t, tKey]);

  useEffect(() => {
    async function fetchHotelDetail() {
      setHotelDetailLoading(true);
      await dispatch(hotelActions.getHotelDetail());
      setHotelDetailLoading(false);
    }
    fetchHotelDetail();
  }, [dispatch]);

  const onBottomTabHandler = (tab) => {
    setActiveTab(tab);
    setViewMap(false);
    if (tab === 1) {
      setTripadvisorVisited(true);
    }
  };

  const renderHotelDetailList = useCallback(
    ({ item }) => {
      const isNonRefUnselectable =
        item?.IsNonRefundable && item?.HotelForRefNonRefUnselectablePolicy;
      const isRefUnselectable = !item?.IsNonRefundable && item?.HotelForRefNonRefUnselectablePolicy;
      const greyOutBookingCom = item?.ShouldGreyOutBookingDotComHotels;
      return (
        <HotelRoomCard
          item={item}
          style={CommonStyles.boxShadow}
          currency={currency}
          currencyCulture={currencyCulture}
          disabled={
            (selectedHotel?.IsSuppressPayNow && roomType == 'payNow') ||
            (selectedHotel?.IsSuppressPayLater && roomType == 'government') ||
            (selectedHotel?.IsSuppressPayLater && roomType == 'payLater') ||
            isRefUnselectable ||
            isNonRefUnselectable ||
            greyOutBookingCom
          }
          onPress={() => {
            dispatch(hotelActions.selectHotelDetail(item));
            dispatch(moveNext());
          }}
        />
      );
    },
    [dispatch, selectedHotel, roomType]
  );

  const renderTripAdvisorLoading = useCallback(
    () => <ActivityIndicator color={Color.primary} />,
    []
  );

  const roomTypeSelectHandler = (roomType) => {
    setRoomType(roomType);
    if (roomType === 'payNow') {
      setActiveDataForList(payNowResults);
    } else if (roomType === 'payLater') {
      setActiveDataForList(payLaterResults);
    } else if (roomType === 'corporate') {
      setActiveDataForList(corporateRates);
    } else if (roomType === 'government') {
      setActiveDataForList(governmentRates);
    }
  };

  return (
    <SafeAreaView style={CommonStyles.flex1}>
      {hotelDetailLoading || !selectedHotel ? (
        <ActivityIndicator size="small" />
      ) : (
        <>
          {defaultResults?.length === 0 ? (
            <View style={[CommonStyles.flex1, CommonStyles.centerAlignment]}>
              <BaseText>{t(tKey('NoRoomsAvailable'))}</BaseText>
            </View>
          ) : (
            <>
              <View style={styles.hotelInfoHeader}>
                <HotelDetailHeader
                  item={selectedHotel}
                  roomTypeLabel={t(tKey(roomType))}
                  price={formatCurrency(
                    activeDataForList && activeDataForList[0] && activeDataForList[0].PerNightCost,
                    currency,
                    currencyCulture
                  )}
                />
              </View>
              <MapView
                style={styles.mapStyle}
                //provider={PROVIDER_GOOGLE}
                initialRegion={{
                  latitude: selectedHotel.Latitude,
                  longitude: selectedHotel.Longitude,
                  latitudeDelta: 0.0922,
                  longitudeDelta: 0.0421,
                }}
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  zoomControl: false,
                  // fullscreenControl: false
                }}
              >
                <PointMarker
                  latitude={selectedHotel.Latitude}
                  longitude={selectedHotel.Longitude}
                  icon="hotel"
                />
                {/* <MapView.Marker
                  coordinate={{
                    latitude: selectedHotel.Latitude,
                    longitude: selectedHotel.Longitude,
                  }}
                /> */}
              </MapView>
              <ScrollView style={viewMap ? styles.viewMapStyle : styles.noViewMapStyle}>
                {photos && photos.length > 0 && (
                  <View style={styles.swiperWrapper}>
                    <Swiper
                      controlsProps={{
                        cellsContent: {
                          top: (
                            <TouchableWithoutFeedback onPress={() => setViewMap((s) => !s)}>
                              <View style={styles.showMapControlContainer}>
                                <View style={styles.gestureHandleControl} />
                                <BaseText style={styles.showMapControlText}>
                                  {viewMap ? t(tKey('HideMap')) : t(tKey('ViewMap'))}
                                </BaseText>
                              </View>
                            </TouchableWithoutFeedback>
                          ),
                        },
                        prevTitle: '',
                        nextTitle: '',
                        dotsWrapperStyle: { display: 'none' },
                      }}
                      minDistanceForAction={0.1}
                    >
                      {photos.map((p) => (
                        <Image
                          key={p}
                          source={{ uri: p }}
                          style={styles.picture}
                          defaultSource={require('../assets/images/image-not-found.png')}
                        />
                      ))}
                    </Swiper>
                  </View>
                )}
                {showCorpMessage && (
                  <View style={styles.corporateDisclaimer}>
                    <Icon name="corporate" color={Color.corporate} size={16} />
                    <BaseText style={styles.textTitle}>{t(tKey('corporateDisclaimer'))}</BaseText>
                  </View>
                )}
                {activeTab === 0 && (
                  <FlatList
                    data={activeDataForList}
                    keyExtractor={(item) => item.GUID}
                    renderItem={renderHotelDetailList}
                    ListHeaderComponent={() => {
                      return roomType === 'default' ? null : (
                        <View style={styles.segmentedControWrapper}>
                          <SegmentedControl
                            items={roomTypeSegments}
                            currency={currency}
                            currencyCulture={currencyCulture}
                            selected={roomType}
                            onChange={roomTypeSelectHandler}
                          />
                        </View>
                      );
                    }}
                  />
                )}
                {tripadvisorVisited && (
                  <ShadowBox style={{ display: activeTab === 1 ? 'flex' : 'none' }}>
                    {Platform.OS === 'web' ? (
                      <iframe src={tripAdvisorUrl} height={'100%'} width={'100%'} />
                    ) : (
                      <WebView
                        originWhitelist={['*']}
                        source={{ uri: tripAdvisorUrl }}
                        style={styles.webView}
                        renderLoading={renderTripAdvisorLoading}
                        startInLoadingState
                      />
                    )}
                  </ShadowBox>
                )}
                {activeTab === 2 && (
                  <ShadowBox style={CommonStyles.p3}>
                    <BaseText>{hotelDescription ? hotelDescription : t(tKey('N/A'))}</BaseText>
                  </ShadowBox>
                )}
                {activeTab === 3 && (
                  <ShadowBox style={CommonStyles.p3}>
                    {hotelAmenities?.length &&
                      hotelAmenities.map((amenity, index) => (
                        <RowView key={index} style={CommonStyles.centerItems}>
                          <Icon name="check" style={CommonStyles.mr1} color={Color.primary}></Icon>
                          <BaseText>{amenity}</BaseText>
                        </RowView>
                      ))}
                    {hotelAmenities.length === 0 && (
                      <RowView style={CommonStyles.centerItems}>
                        <BaseText>{t(tKey('N/A'))}</BaseText>
                      </RowView>
                    )}
                  </ShadowBox>
                )}
              </ScrollView>
              <TabBar
                onChange={onBottomTabHandler}
                selected={activeTab}
                items={[
                  { value: 0, second: t(tKey('Rooms')), icon: 'room' },
                  { value: 1, second: t(tKey('Reviews')), icon: 'message' },
                  { value: 2, second: t(tKey('Details')), icon: 'list' },
                  { value: 3, second: t(tKey('CoolStuff')), icon: 'glasses' },
                ]}
              />
            </>
          )}
        </>
      )}
    </SafeAreaView>
  );
};

const heightHeaderCard = scale(60);
const swiperHeight = scale(200);
const styles = StyleSheet.create({
  mapStyle: {
    ...StyleSheet.absoluteFillObject,
    top: heightHeaderCard,
  },
  hotelInfoHeader: {
    height: heightHeaderCard,
    flexDirection: 'row',
    overflow: 'hidden',
    backgroundColor: Color.accent,
    ...CommonProperties.py1,
    ...CommonProperties.px3,
  },
  viewMapStyle: {
    marginTop: scale(400),
  },
  noViewMapStyle: {
    ...CommonProperties.mt4,
  },
  showMapControlContainer: {
    flex: 1,
    alignItems: 'center',
  },
  /* FIXME: Check if the styles.gestureHandleControl are correct for this view*/
  gestureHandleControl: {
    width: scale(20),
    height: scale(4),
    backgroundColor: Color.accent,
    ...CommonProperties.borderRadius,
    ...CommonProperties.my1,
  },
  showMapControlText: {
    color: Color.accent,
  },
  picture: {
    height: swiperHeight,
    ...CommonProperties.borderRadius,
  },
  swiperWrapper: {
    height: swiperHeight,
    ...CommonProperties.mx3,
  },
  corporateDisclaimer: {
    backgroundColor: Color.accent,
    ...CommonProperties.mx3,
    ...CommonProperties.mt3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.p3,
  },
  textTitle: {
    color: Color.primary,
    fontSize: 20,
    marginLeft: '1%',
  },
  segmentedControWrapper: {
    ...CommonProperties.my3,
    ...CommonProperties.mx3,
  },
  webView: {
    flex: 1,
    height: scale(150),
  },
});

export default HotelDetailScreen;
